// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { LMModel } from '../components/form/model/lm-model';
import { LMModelField } from '../components/form/model/lm-model-field';
import { uniq } from 'lodash-es';

@Injectable()
export class LMCommonUtilService {
  private lplColor: Array<string> = [ 'black', 'blue', 'brown', 'gray', 'green', 'jade', 'magenta', 'maroon', 'orange', 'red', 'yellow' ];

  constructor() {  }

  // converts LPL color to UX color
  public convertColor(params: any) {
    // params = color
    const color: string = params.color;

    // validate color; < 0 means not found
    if (this.lplColor.indexOf(color) < 0) {
      return null;
    }

    switch (color) {
      case 'black':
        return 'graphite10';

      // no brown color in soho
      case 'brown':
        return 'graphite05';

      case 'gray':
        return 'graphite03';

      case 'green':
        return 'emerald07';

      case 'jade':
        return 'emerald03';

      case 'magenta':
        return 'ruby03';

      case 'maroon':
        return 'ruby05';

      case 'orange':
        return 'amber07';

      case 'red':
        return 'ruby07';

      case 'yellow':
        return 'amber03';

      // falls through to return
      // tslint allow fall through
      /* tslint:disable */
      case 'blue':
      default:
        return 'azure07';
      /* tslint:enable */
    }
  }

  // converts UX color to state for use with LMProgress
  public convertColorToState(params: any) {
    // params > color
    const color = params.color.toLowerCase();

    // green
    if (color.indexOf('emerald') > -1) {
      return 'good';
    }

    // red
    // uses error-color so error icon does not display
    if (color.indexOf('ruby') > -1) {
      return 'error-color';
    }

    // yellow
    if (color.indexOf('amber') > -1) {
      return 'alert-yellow';
    }

    // blue
    return 'primary';
  }

  // converts UX alert color to SohoAlertTypes
  public convertAlertColorToAlertType(alertColor: string) {

    switch (alertColor) {
      case 'redAlert':
        return 'error';

      case 'yellowAlert':
        return 'alert';

      case 'greenAlert':
        return 'success';

      // falls through to return
      // tslint allow fall through
      // tslint:disable
      case 'blueAlert':
      default:
        return 'info';
        // tslint:enable

      case 'iconAlert':
        return 'icon';
    }
  }

  /**
   * Processes the range object returning the color
   * @param ranges
   * @param value
   * @param data
   * @returns
   */
  public getRangeColor(ranges: LMRanges, value: number, data: LMDataView | LMModel): string {

    // private function to getRangeColor()
    // tslint:disable-next-line:no-shadowed-variable
    const checkRangeValue = (value: any, data: LMDataView | LMModel) => {
      // parseFloat is ok here as values are in Landmark format
      const defaultValue = 0;

      if (value === null || value === undefined) {
        return defaultValue;
      }

      if (!isNaN(parseFloat(value))) {
        return parseFloat(value);
      }

      if (!data) {
        return defaultValue;
      }

      const isModel: boolean = (data instanceof LMModel);

      if (isModel) {
        const dataField: LMModelField = (data as LMModel).getField(value);
        if (!dataField) {
          return defaultValue;
        }

        return parseFloat(dataField.getData());
      }

      if (!(data as LMDataView).fields.hasOwnProperty(value)) {
        return defaultValue;
      }

      return parseFloat((data as LMDataView).fields[ value ].value.toString());
    };

    let color = '';

    if (ranges) {
      color = Object.keys(ranges).filter((item, index, ary) => {
        const start = checkRangeValue(ranges [ item ][ 'start' ], data);
        const end = checkRangeValue(ranges[ item ][ 'end' ], data);

        // val, start, and end should all be numeric at this point
        if (value >= start && value <= end) {
          return item;
        }
      }).map((item, index, ary) => this.convertColor({ color: item })).join('');
    }

    if (color === '') {
      color = this.convertColor({ color: 'blue' });
    }

    return this.convertColorToState({ color });
  }

  public getBindIdsFromFormSpec(formSpec: LMFormSpec, specFields?: LMSpecFields): Array<string> {
    const layout = JSON.stringify(formSpec)
      .split('"busFormLayoutSpec":')
      .filter((item, index, ary) => {
        if (index > 0) {
          return item;
        }
      })
      .join('');

    let bindIds = this._getBindIds(layout);
    bindIds = this._applySpecFields(bindIds, specFields);
    return uniq(bindIds);
  }

  public getBindIdsFromLayout(layout: LMFormLayout, specFields?: LMSpecFields): Array<string> {
    let bindIds = this._getBindIds(JSON.stringify(layout));
    bindIds = this._applySpecFields(bindIds, specFields);
    return uniq(bindIds);
  }

  private _getBindIds(layout: string): Array<string> {
    return layout
      .split('"bindId":"')
      .filter((item, index, ary) => {
        if (index > 0) {
          return item;
        }
      })
      .map((item, index, ary) => item.substr(0, item.indexOf('"')));
  }

  public clearCodeBlockDataViewFields(formSpec: LMFormSpec, layout: LMFormLayout, dataView: LMDataView) {
    const busFormSpec = formSpec.busFormSpec;
    const fieldIds: string[] = this.getBindIdsFromLayout(layout, busFormSpec.fields);

    // Set the dataView field value to blank
    fieldIds.forEach(fieldId => {
      this.clearCodeBlockDataViewField(fieldId, dataView);
    });
  }

  private clearCodeBlockDataViewField(fieldId: string, dataView: LMDataView) {
    if (dataView && dataView.fields) {
      if (!!dataView.fields[ fieldId ]) {
        dataView.fields[ fieldId ].value = '';

        // iterate member fields to clear the value and if available, the stateValues property
        if (dataView.fields[ fieldId ].members) {
          dataView.fields[ fieldId ].members.forEach( key => {
            dataView.fields[ key ].value = '';

            if (dataView.fields[ key ].stateValues) {
              dataView.fields[ key ].stateValues = undefined;
            }
          });
        }
      }
    }
  }

  private _applySpecFields(bindIds: Array<string>, specFields?: LMSpecFields): Array<string> {
    let fieldIds: Array<string> = [];

    // if specFields are passed, then will add fields from the compact format layout
    if (specFields) {
      bindIds.forEach( (fieldId) => {
        if (specFields[fieldId] && specFields[fieldId].compactFormat && specFields[fieldId].layout) {
          // one of the fields is a compound group; add compact format layout fields to the array
          const memberFieldIds = this._getBindIds(JSON.stringify(specFields[fieldId].layout));
          fieldIds = [...fieldIds, ...memberFieldIds];
        }
      });
    }

    return [...bindIds, ...fieldIds];
  }

  // Need to pass in element from a nativeElement
  public scrollIntoView(element: HTMLElement) {
    let scrollableParent;

    if (element?.parentElement) {
      scrollableParent = this.getScrollableParent(element.parentElement);
    }

    if (scrollableParent) {
      if (this.isOutOfView(element, scrollableParent)) {
        scrollableParent.scrollTop = element.offsetTop;
      }
    }
  }

  // Gets the next scrollable parent ancestor
  private getScrollableParent(element: HTMLElement): HTMLElement {
    if (!element) {
      return null;
    }

    const overflowY = window.getComputedStyle(element).overflowY;
    const isScrollable = !(overflowY.includes('hidden') || overflowY.includes('visible'));
    if (isScrollable && element.scrollHeight > element.clientHeight) {
      return element;
    }
    return this.getScrollableParent(element.parentElement);
  }

  private isOutOfView(element: HTMLElement, scrollableParent: HTMLElement) {
    if (element.offsetTop < 0) {
      return true;
    }

    const containerRect = scrollableParent.getBoundingClientRect();
    const targetRect = element.getBoundingClientRect();

    const isOutOfView = targetRect.bottom < containerRect.top || targetRect.top > containerRect.bottom;
    if (isOutOfView) {
      return true;
    }

    return false;
  }
}
